import type { SyntheticEvent } from 'react';
import type { FieldPath, FieldValues, UseFormSetError } from 'react-hook-form';
import type { ApiRequestError } from '@/lib/apiClient';

/**
 * The error type a server-reported field error carries.
 *
 * Kept apart from the client's own checks (`useLiveFormErrors`): the browser
 * cannot re-ask the server whether an email is still taken, so such an error
 * stays until that field is edited or the form is sent again — re-running the
 * client schema would pass and wrongly wipe it.
 */
export const SERVER_ERROR_TYPE = 'server';

/**
 * Puts the server's per-field errors back on the fields they belong to.
 *
 * Fourteen screens had written this loop out, in two spellings, and it is the
 * line between a form that tells somebody which box to fix and one that shows a
 * toast and leaves them hunting. A toast fades; the field does not (§6).
 *
 * The server is the authority on validity — the client's schema is for fast
 * feedback only — so whatever it names is trusted here and cast to a field
 * path. A key the form has no field for is set and simply never rendered,
 * which is better than dropping the only account of what was wrong.
 */
export function applyFieldErrors<T extends FieldValues>(
  setError: UseFormSetError<T>,
  error: ApiRequestError,
): void {
  for (const [field, message] of Object.entries(error.fieldMap)) {
    setError(field as FieldPath<T>, { type: SERVER_ERROR_TYPE, message });
  }
}

/** An Aadhaar number is twelve digits. */
export const AADHAAR_DIGITS = 12;

/**
 * An Aadhaar number as the card prints it — `2345 6789 0123`. Anything that is
 * not a digit is dropped and a thirteenth digit never appears, so whatever is
 * typed or pasted lands in the same shape. The schemas strip the spaces again
 * before validating, so the server only ever sees the twelve digits.
 */
export function formatAadhaar(value: string): string {
  return value
    .replace(/\D/g, '')
    .slice(0, AADHAAR_DIGITS)
    .replace(/(\d{4})(?=\d)/g, '$1 ');
}

/** An Indian mobile number is ten digits. */
export const MOBILE_DIGITS = 10;

/**
 * What a mobile-number box may hold: digits only, never more than ten.
 *
 * Applied as the person types or pastes, so an eleventh digit simply does not
 * appear rather than being reported after the fact. A number pasted with its
 * country code or trunk zero — `+91 98765 43210`, `098765 43210` — keeps the
 * ten digits that matter instead of the first ten.
 */
export function toMobileDigits(value: string): string {
  let digits = value.replace(/\D/g, '');
  if (digits.length > MOBILE_DIGITS && digits.startsWith('91')) digits = digits.slice(2);
  if (digits.length > MOBILE_DIGITS && digits.startsWith('0')) digits = digits.slice(1);
  return digits.slice(0, MOBILE_DIGITS);
}

const refuseClipboard = (event: SyntheticEvent) => event.preventDefault();

/**
 * Spread on a new-password field: its value cannot be copied or cut out.
 *
 * Paired with `CONFIRMATION_NO_PASTE`. The eye toggle shows the password as
 * plain text, and a confirmation filled by copying the first box only proves
 * the clipboard works — a typo in the first box goes straight into the account.
 */
export const PASSWORD_NO_COPY = { onCopy: refuseClipboard, onCut: refuseClipboard };

/**
 * Spread on a confirm-password field: nothing can be pasted or dropped in, so
 * the password is typed a second time. Say so in the field's hint — a paste
 * that silently does nothing reads as a broken box. Autofill from a password
 * manager is not a paste and still works.
 */
export const CONFIRMATION_NO_PASTE = { onPaste: refuseClipboard, onDrop: refuseClipboard };
